import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';
import type ViewCategoryStore from '../../AppData/_stores/ViewCategoryStore';
import { alpha, Button, Collapse, Divider, Stack, Typography, styled } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ProductCatalogCollectionNavTone } from '../Collections/ProductCatalogCollection';
import useProductCatalogCategories from '../hooks/useProductCatalogCategories';
import useProductCatalogCollections from '../hooks/useProductCatalogCollections';
import useSelectedProductCatalogCollection from '../hooks/useSelectedProductCatalogCollection';

type CategorySelectorVariant = 'sidebar' | 'sheet';

interface ProductCatalogCategorySelectorProps {
	variant?: CategorySelectorVariant
	onNavigate?: () => void
}

interface CategoryLinkProps {
	isSelected?: boolean
	level?: 'parent' | 'child'
	tone?: ProductCatalogCollectionNavTone
}

interface CategorySelectorShellProps {
	selectorVariant: CategorySelectorVariant
}

const getCategoryItemStyles = (theme: Theme, isSelected?: boolean, level: CategoryLinkProps['level'] = 'parent', tone?: CategoryLinkProps['tone']) => {
	const toneColor = tone === 'bestseller' || tone === 'sale'
		? theme.palette.accent.main
		: tone === 'new'
			? theme.palette.primary.main
			: undefined;
	const activeColor = toneColor ?? theme.palette.secondary.main;

	return {
		alignItems: 'center',
		backgroundColor: isSelected ? alpha(activeColor, 0.07) : 'transparent',
		borderRadius: 8,
		color: toneColor !== undefined ? activeColor : isSelected === true ? activeColor : theme.palette.text.primary,
		display: 'flex',
		fontSize: level === 'child' ? '14px' : '15px',
		gap: theme.spacing(1),
		fontWeight: isSelected ? 600 : 500,
		minHeight: level === 'child' ? 38 : 46,
		padding: theme.spacing(level === 'child' ? 0.75 : 1, level === 'child' ? 1.5 : 1.25),
		textDecoration: 'none',
		transition: 'background-color 150ms ease, color 150ms ease',
		width: '100%',

		'&:hover': {
			backgroundColor: isSelected ? alpha(activeColor, 0.09) : alpha(activeColor, 0.04),
			color: activeColor,
			textDecoration: 'none',
		},

		'& > .category-link-badge-icon': {
			flexShrink: 0,
			fontSize: 18,
		},

		'& > .category-link-expand-icon': {
			fontSize: 18,
			marginLeft: 'auto',
			transition: 'transform 150ms ease',
		},
	}
}

const CategoryLink = styled(Link, {
	shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'level' && prop !== 'tone',
})<CategoryLinkProps>(({ theme, isSelected, level = 'parent', tone }) => {
	return getCategoryItemStyles(theme, isSelected, level, tone)
});

const CategoryButton = styled('button', {
	shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'level' && prop !== 'tone',
})<CategoryLinkProps>(({ theme, isSelected, level = 'parent', tone }) => ({
	...getCategoryItemStyles(theme, isSelected, level, tone),
	appearance: 'none',
	border: 0,
	cursor: 'pointer',
	fontFamily: 'inherit',
	textAlign: 'left',
}));

const CategorySelectorShell = styled(Stack, {
	shouldForwardProp: (prop) => prop !== 'selectorVariant',
})<CategorySelectorShellProps>(({ selectorVariant, theme }) => ({
	padding: selectorVariant === 'sheet' ? 0 : theme.spacing(0, 2),

	...(selectorVariant === 'sidebar' ? {
		[theme.breakpoints.up('sm')]: {
			paddingTop: theme.spacing(4),
		},
	} : {}),
}));

const MobileCategoryToggle = styled(Button)(({ theme }) => ({
	justifyContent: 'space-between',
	minHeight: 52,
	paddingLeft: theme.spacing(2),
	paddingRight: theme.spacing(2),

	[theme.breakpoints.up('sm')]: {
		display: 'none',
	},
}));

const ChildCategoryList = styled(Stack)(({ theme }) => ({
	padding: theme.spacing(0.25, 0, 1.25, 1.5),
}));

const CategorySectionTitle = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontSize: 12,
	fontWeight: 700,
	letterSpacing: 0,
	lineHeight: 1.35,
	padding: theme.spacing(0.75, 1.25, 0.25),
}));

export default function ProductCatalogCategorySelector({ variant = 'sidebar', onNavigate }: ProductCatalogCategorySelectorProps) {
	const [displayDrawer, setDisplayDrawer] = useState(false);
	const [expandedCategoryId, setExpandedCategoryId] = useState<number | null>(null);
	const [t] = useTranslation('products')
	const [tR] = useTranslation('routes');
	const params = useParams()
	const [searchParams] = useSearchParams();
	const {
		childrenByParentId,
		getCategoryUrlSlug,
		topLevelCategories,
		topLevelCategoriesByUrlSlug,
	} = useProductCatalogCategories();
	const productCatalogCollections = useProductCatalogCollections();
	const selectedCollection = useSelectedProductCatalogCollection();
	const preservedSearch = searchParams.toString();

	const getCategoryPath = useCallback((path: string) => {
		return preservedSearch === '' ? path : path + '?' + preservedSearch;
	}, [preservedSearch])

	const selectedTopLevelCategory = params.category1 !== undefined && selectedCollection === undefined
		? topLevelCategoriesByUrlSlug.get(params.category1)
		: undefined;

	useEffect(() => {
		if (variant !== 'sheet') {
			return;
		}

		setExpandedCategoryId(selectedTopLevelCategory?.get('id') ?? null);
	}, [selectedTopLevelCategory, variant])

	const onCategoryNavigate = useCallback(() => {
		setDisplayDrawer(false);
		onNavigate?.();
	}, [onNavigate])

	const renderTopLevelCategory = useCallback((topLevelCategory: ViewCategoryStore) => {
		const childCategories = childrenByParentId.get(topLevelCategory.get('id')) ?? [];
		const selected = params.category2 ? childCategories.some(category => getCategoryUrlSlug(category) === params.category2) : params.category1 === getCategoryUrlSlug(topLevelCategory)
		const childCategoryCount = childCategories.length;
		const expanded = variant === 'sheet'
			? expandedCategoryId === topLevelCategory.get('id')
			: selected;
		const topLevelCategoryUrlSlug = getCategoryUrlSlug(topLevelCategory);
		const categoryPath = getCategoryPath(tR('/products') + '/' + topLevelCategoryUrlSlug);
		const toggleExpandedCategory = () => setExpandedCategoryId(value => value === topLevelCategory.get('id') ? null : topLevelCategory.get('id'));

		return <Stack key={topLevelCategory.get('id')} spacing={0.25}>
			{variant === 'sheet' && childCategoryCount > 0 ? <CategoryButton
				aria-expanded={expanded}
				isSelected={selected}
				type="button"
				onClick={toggleExpandedCategory}
			>
				<span>{topLevelCategory.get('name')}</span>
				<ExpandMoreIcon
					className="category-link-expand-icon"
					sx={{
						transform: expanded ? 'rotate(180deg)' : undefined,
					}}
				/>
			</CategoryButton> : <CategoryLink
				aria-current={selected ? 'page' : undefined}
				isSelected={selected}
				to={categoryPath}
				onClick={onCategoryNavigate}
			>
				<span>{topLevelCategory.get('name')}</span>
				{childCategoryCount > 0 ? <ExpandMoreIcon
					className="category-link-expand-icon"
					sx={{
						transform: expanded ? 'rotate(180deg)' : undefined,
					}}
				/> : null}
			</CategoryLink>}

			{childCategoryCount > 0 ? <Collapse in={expanded} timeout={150} unmountOnExit>
				<ChildCategoryList spacing={0.5}>
					{variant === 'sheet' ? <CategoryLink
						aria-current={selected && params.category2 === undefined ? 'page' : undefined}
						isSelected={selected && params.category2 === undefined}
						level="child"
						to={categoryPath}
						onClick={onCategoryNavigate}
					>{t('View all in {{category}}', { category: topLevelCategory.get('name') })}</CategoryLink> : null}
					{childCategories.map(secondLayer => {
						const secondLayerUrlSlug = getCategoryUrlSlug(secondLayer);

						return <CategoryLink
							key={secondLayer.get('id')}
							to={getCategoryPath(tR('/products') + '/' + topLevelCategoryUrlSlug + '/' + secondLayerUrlSlug)}
							aria-current={params.category2 === secondLayerUrlSlug ? 'page' : undefined}
							isSelected={params.category2 === secondLayerUrlSlug}
							level="child"
							onClick={onCategoryNavigate}
						>{secondLayer.get('name')}</CategoryLink>
					})}
				</ChildCategoryList>
			</Collapse> : null}
		</Stack>
	}, [childrenByParentId, expandedCategoryId, getCategoryPath, getCategoryUrlSlug, onCategoryNavigate, params.category1, params.category2, t, tR, variant])

	return <CategorySelectorShell
		selectorVariant={variant}
		spacing={variant === 'sheet' ? 1 : 1.5}
		sx={{ pb: variant === 'sheet' ? 0 : { xs: displayDrawer ? 2 : 0, sm: 0 } }}
	>
		{variant === 'sidebar' ? <MobileCategoryToggle
			color="secondary"
			endIcon={<ExpandMoreIcon
				sx={{
					transform: displayDrawer ? 'rotate(180deg)' : undefined,
					transition: 'transform 150ms',
				}}
			/>}
			onClick={() => setDisplayDrawer(value => !value)}
		>
			{t('Categories')}
		</MobileCategoryToggle> : null}
		<Stack spacing={0.5} sx={{ display: variant === 'sheet' ? 'flex' : { xs: displayDrawer ? 'flex' : 'none', sm: 'flex' } }}>
			<CategoryLink
				aria-current={params.category1 === undefined ? 'page' : undefined}
				isSelected={params.category1 === undefined}
				to={getCategoryPath(tR('/products'))}
				onClick={onCategoryNavigate}
			>
				<GridViewRoundedIcon className="category-link-badge-icon" />
				<span>{t('All Products')}</span>
			</CategoryLink>
			{productCatalogCollections.map(collection => {
				const CollectionIcon = collection.navIcon;
				const selected = selectedCollection?.slug === collection.slug;

				return <CategoryLink
					key={collection.slug}
					aria-current={selected ? 'page' : undefined}
					isSelected={selected}
					tone={collection.navTone}
					to={getCategoryPath(tR('/products') + '/' + collection.slug)}
					onClick={onCategoryNavigate}
				>
					{CollectionIcon !== undefined ? <CollectionIcon className="category-link-badge-icon" /> : null}
					{collection.title(t)}
				</CategoryLink>
			})}
			<Divider color="#e2e2e2" />
			<CategorySectionTitle component="p">{t('Categories')}</CategorySectionTitle>
			{topLevelCategories.map(renderTopLevelCategory)}
		</Stack>
	</CategorySelectorShell>
}
